import { useEffect, useRef, useState } from 'react';
import styles from './ExpressionButton.module.scss';
import { FaBook, FaCode, FaPaste } from 'react-icons/fa';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '../modal/Modal';
import Button from '../button/Button';
import { Header, Label } from '../texts/Texts';
import { useTranslation } from 'react-i18next';
import ExpressionSystem, { ExpressionParameter, ExpressionResult, FormatType } from './ExpressionSystem';
import ExpressionInput, { ExpressionInputRef } from './ExpressionInput';
import useNoInitialEffect from 'common/hooks/useNoInitialEffect';
import { BODY_TOOLTIP } from 'Config';
import { IoCopyOutline } from 'react-icons/io5';
import Storage from 'common/services/Storage';
import Toast from 'common/services/Toast';
import { numericFormatter } from 'react-number-format';
import FunctionReferenceModal from './FunctionReferenceModal';

const EXPRESSION_STORAGE_KEY = 'expression';

function formatNumber(val: string | null | undefined, decimalScale?: number, type?: FormatType) {
    return numericFormatter(val || '0', {
        suffix: type === 'plain' ? '' : (type === 'percentage' ? ' %' : ' €'),
        thousandSeparator: ' ',
        decimalSeparator: ',',
    })
}

export interface ExpressionButtonProps {
    value: string
    absolute?: boolean;
    type?: FormatType;
    disabled?: boolean;
    decimalScale?: number;
    calculatedValue?: number | null;
    title: string;
    hideResult?: boolean;
    loadParameters: () => ExpressionParameter[];
    onChange: (expression: string | null, value: number | null | undefined) => void;
    onValidate?: (expression: string) => string | null;
    onOpen?: () => void;
}

const ExpressionButton = ({
    value,
    absolute,
    decimalScale,
    type,
    title,
    disabled,
    hideResult,
    calculatedValue,
    loadParameters,
    onChange,
    onValidate,
    onOpen,
}: ExpressionButtonProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showFunctionRef, setShowFunctionRef] = useState(false);
    const { t, i18n } = useTranslation();
    const [expression, setExpression] = useState('');
    const [parameters, setParameters] = useState<ExpressionParameter[]>([]);
    const [result, setResult] = useState<ExpressionResult | null>(null);
    const inputRef = useRef<ExpressionInputRef>(null);

    function handleButtonClick() {
        if (onOpen) {
            onOpen();
            return;
        }
        // TODO: adicionar um loading
        const params = loadParameters();
        setParameters(params);
        const translated = ExpressionSystem.translateFormula(value, i18n.language);
        setExpression(translated);
        setResult(ExpressionSystem.evaluate(value, params));
        setIsOpen(true);
    }

    useEffect(() => {
        if (isOpen && inputRef.current) {
            const translated = ExpressionSystem.translateFormula(value, i18n.language);
            if (translated !== expression) {
                inputRef.current.updateValue(translated);
                setExpression(translated);
            }
        }
    }, [isOpen, value]);

    function onCancel() {
        setExpression(ExpressionSystem.translateFormula(value, i18n.language));
        setResult(ExpressionSystem.evaluate(value, parameters));
        setIsOpen(false);
    }

    function onSave() {
        if (onValidate) {
            const error = onValidate(expression);
            if (error) {
                Toast.warning(error, 8000);
                return;
            }
        }
        const normalized = ExpressionSystem.normalizeToEnglish(expression);
        onChange(normalized, result ? result.value : null);
        setIsOpen(false);
    }

    function handleChange(expr: string | null) {
        const newExpression = expr ?? '';

        setExpression(newExpression);
        setResult(ExpressionSystem.evaluate(newExpression, parameters));
    }

    function copyExpression() {
        const normalized = ExpressionSystem.normalizeToEnglish(expression);
        Storage.set(EXPRESSION_STORAGE_KEY, normalized);
        // Store parameter name map for cross-family paste
        const nameMap: Record<string, string> = {};
        for (const p of parameters) {
            const serializedId = ExpressionSystem.serializeParameterMentionId(p.id);
            nameMap[serializedId] = p.name;
        }
        Storage.set(EXPRESSION_STORAGE_KEY + '_names', JSON.stringify(nameMap));
        navigator.clipboard.writeText(normalized);
    }

    function pasteExpression() {
        const pasteExpr = Storage.get(EXPRESSION_STORAGE_KEY);
        if (pasteExpr) {
            const translated = ExpressionSystem.translateFormula(pasteExpr, i18n.language);
            const nameMapJson = Storage.get(EXPRESSION_STORAGE_KEY + '_names');
            const nameMap: Record<string, string> | undefined = nameMapJson ? JSON.parse(nameMapJson) : undefined;
            inputRef.current?.updateValue(translated, nameMap);
            setExpression(translated);
        }
    }

    useNoInitialEffect(() => {
        const translated = ExpressionSystem.translateFormula(value, i18n.language);
        if (expression !== translated) {
            setExpression(translated);
        }
    }, [value, decimalScale]);

    // Re-evaluate expression when the calculated value changes (e.g., dependent fields updated)
    // to keep the error state in sync
    useNoInitialEffect(() => {
        if (!isOpen && value && value.trim().length > 0) {
            const params = loadParameters();
            setResult(ExpressionSystem.evaluate(value, params));
        }
    }, [calculatedValue]);

    const hasFormula = value && value.trim().length > 0;
    const hasError = result?.error;
    const hasValidFormula = hasFormula && !hasError;

    return (
        <div className={`${styles.container} ${absolute ? styles.absolute : ''}`}>
            <span
                className={`${styles.button} ${hasError ? styles.buttonHasError : ''} ${hasValidFormula ? styles.buttonHasFormula : ''} ${isOpen ? styles.buttonIsOpen : ''}`}
                onClick={handleButtonClick}
            >
                <FaCode />
            </span>

            <FunctionReferenceModal
                isOpen={showFunctionRef}
                onClose={() => setShowFunctionRef(false)}
                onSelect={(functionName) => {
                    inputRef.current?.insertTextAtCursor(functionName + '(');
                }}
            />
            {isOpen && <Modal style={{ content: { minWidth: 400 } }} isOpen={true} onRequestClose={() => onCancel()}>
                <div>
                    <ModalHeader>
                        <Header>
                            {title}
                        </Header>
                    </ModalHeader>
                    <ModalBody style={{ overflow: 'visible' }}>
                        <div>
                            {!hideResult && (
                                <div className={styles.resultContainer}>
                                    <Label className={styles.resultLabel}>{t('common.result')}:</Label>
                                    {(result?.error ? 'ERROR' : formatNumber(ExpressionSystem.numberToStr(result?.value), decimalScale, 'plain'))}
                                </div>
                            )}
                            {isOpen && (
                                <ExpressionInput
                                    ref={inputRef}
                                    value={expression}
                                    parameters={parameters}
                                    onChange={handleChange}
                                    disabled={disabled}
                                />
                            )}
                        </div>
                    </ModalBody>
                    <ModalFooter className={styles.footer}>
                        <div className={styles.footerLeft}>
                            <Button
                                fw
                                variant='third'
                                onClick={copyExpression}
                                data-tooltip-id={BODY_TOOLTIP}
                                data-tooltip-content={t('common.copy')}
                            >
                                <IoCopyOutline />
                            </Button>
                            {!disabled && <Button
                                fw
                                variant='third'
                                onClick={pasteExpression}
                                data-tooltip-id={BODY_TOOLTIP}
                                data-tooltip-content={t('common.paste')}
                            >
                                <FaPaste />
                            </Button>}
                            {!disabled && <Button
                                fw
                                variant='third'
                                onClick={() => setShowFunctionRef(true)}
                                data-tooltip-id={BODY_TOOLTIP}
                                data-tooltip-content={t('common.function_reference')}
                            >
                                <FaBook />
                            </Button>}
                        </div>
                        <div>
                            <Button fw variant='secondary' onClick={() => onCancel()}>{t('common.cancel')}</Button>
                            {!disabled && <Button variant="primary" onClick={() => onSave()}>{t('common.save')}</Button>}
                        </div>
                    </ModalFooter>
                </div>
            </Modal>}
        </div>
    );
};

export default ExpressionButton;
